import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
// Byt till centrala hooks
import {
  useMatchesSummary,
  useOfficialMatches,
  selectUpcomingByDate,
} from "../queries/matches";
import { usePlayoffs, useStandings } from "../queries/standings";
import { useSystemAnnouncements } from "@/queries/system";
// Skeletons
import HeroSkeleton from "../components/skeletons/HeroSkeleton";
import StatCardSkeleton from "../components/skeletons/StatCardSkeleton";
import MatchesListSkeleton from "../components/skeletons/MatchesListSkeleton";
import TeamTableSkeleton from "../components/skeletons/TeamTableSkeleton";

import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem } from "../components/ui/accordion";
import TeamTable from "../components/TeamTable";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  CalendarDays,
  Clock3,
  Users,
  List as ListIcon,
  Trophy,
  LoaderCircle,
  ChevronLeft,
  ChevronRight,
  Megaphone,
  AlertTriangle,
  CheckCircle2,
  AlertCircle,
  X,
  ChevronDown,
} from "lucide-react";

import PageTutorial from "@/components/tutorial/PageTutorial";
import { formatSvDate, formatSvDateTime, formatSvTime, isSameSvDay, toDate, getSvYear } from "@/utils/date";
import { getConfiguredSeason } from "@/lib/season";
import AdSlot from "@/components/AdSlot";
import TeamLogo from "@/components/TeamLogo";
import Seo from "@/components/Seo";
import { getNativePlatform } from "@/lib/runtime";
import { useOpenOfficialMatch } from "@/hooks/useOpenOfficialMatch";

import NativeHomePage from "@/components/native/NativeHomePage";

const ANNOUNCEMENT_STORAGE_KEY = "app:dismissedAnnouncementIds";

const ANNOUNCEMENT_VARIANTS = {
  info: {
    icon: Megaphone,
    label: "Information",
    iconClass: "text-sky-700 dark:text-sky-300",
    cardClass: "border-sky-300/80 bg-sky-50/90 text-sky-950 dark:border-sky-400/30 dark:bg-sky-500/10 dark:text-sky-50",
    softClass: "text-sky-800/80 dark:text-sky-200/80",
    buttonClass: "border-sky-300/70 hover:bg-sky-100/80 dark:border-sky-400/30 dark:hover:bg-sky-500/10",
  },
  success: {
    icon: CheckCircle2,
    label: "Klart",
    iconClass: "text-emerald-700 dark:text-emerald-300",
    cardClass: "border-emerald-300/80 bg-emerald-50/90 text-emerald-950 dark:border-emerald-400/30 dark:bg-emerald-500/10 dark:text-emerald-50",
    softClass: "text-emerald-800/80 dark:text-emerald-200/80",
    buttonClass: "border-emerald-300/70 hover:bg-emerald-100/80 dark:border-emerald-400/30 dark:hover:bg-emerald-500/10",
  },
  warning: {
    icon: AlertTriangle,
    label: "Varning",
    iconClass: "text-amber-700 dark:text-amber-300",
    cardClass: "border-amber-300/80 bg-amber-50/90 text-amber-950 dark:border-amber-400/30 dark:bg-amber-500/10 dark:text-amber-50",
    softClass: "text-amber-800/80 dark:text-amber-200/80",
    buttonClass: "border-amber-300/70 hover:bg-amber-100/80 dark:border-amber-400/30 dark:hover:bg-amber-500/10",
  },
  error: {
    icon: AlertCircle,
    label: "Fel",
    iconClass: "text-red-700 dark:text-red-300",
    cardClass: "border-red-300/80 bg-red-50/90 text-red-950 dark:border-red-400/30 dark:bg-red-500/10 dark:text-red-50",
    softClass: "text-red-800/80 dark:text-red-200/80",
    buttonClass: "border-red-300/70 hover:bg-red-100/80 dark:border-red-400/30 dark:hover:bg-red-500/10",
  },
};

import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "../components/ui/carousel";

/* ---------- Små hjälpkort ---------- */
function StatCard({ title, value, hint, icon, onClick }) {
  const content = (
    <div className="h-full rounded-sm border border-border bg-card px-4 p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-muted-foreground">{title}</span>
        {icon ? <span className="text-muted-foreground/80">{icon}</span> : null}
      </div>
      <div className="text-2xl font-semibold text-foreground">{value}</div>
      {hint ? <div className="text-xs text-[hsl(var(--muted))] mt-1">{hint}</div> : null}
    </div>
  );

  if (typeof onClick !== "function") return content;

  return (
    <button type="button" onClick={onClick} className="h-full w-full text-left transition-transform hover:-translate-y-0.5">
      {content}
    </button>
  );
}

function isManualCancelled(match) {
  return String(match?.manual_status || "").trim().toLowerCase() === "cancelled";
}

function AnnouncementCardOld({
  announcement,
  expanded,
  onToggleExpand,
  onDismiss,
}) {
  const variant = ANNOUNCEMENT_VARIANTS[announcement?.level] || ANNOUNCEMENT_VARIANTS.info;
  const AnnouncementIcon = variant.icon;
  const title = String(announcement?.title || "").trim() || "Meddelande";
  const message = String(announcement?.message || "").trim();
  const canExpand = message.length > 140 || message.includes("\n");
  const [showMessageContent, setShowMessageContent] = React.useState(expanded);

  React.useEffect(() => {
    if (expanded) {
      const timeoutId = window.setTimeout(() => {
        setShowMessageContent(true);
      }, 180);
      return () => window.clearTimeout(timeoutId);
    }

    setShowMessageContent(false);
    return undefined;
  }, [expanded]);

  return (
    <div className={`relative overflow-hidden rounded-2xl border shadow-sm ${variant.cardClass}`}>
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <AnnouncementIcon className={`h-5 w-5 shrink-0 ${variant.iconClass}`} aria-hidden />
              <span className={`min-w-0 truncate text-sm font-semibold leading-snug ${variant.softClass}`}>
                {title}
              </span>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {canExpand ? (
              <button
                type="button"
                onClick={onToggleExpand}
                className={`inline-flex h-8 w-8 items-center justify-center rounded-full border transition ${variant.buttonClass}`}
                aria-label={expanded ? "Minimera meddelande" : "Expandera meddelande"}
              >
                <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
              </button>
            ) : null}
            {announcement?.dismissible ? (
              <button
                type="button"
                onClick={onDismiss}
                className={`inline-flex h-8 w-8 items-center justify-center rounded-full border transition ${variant.buttonClass}`}
                aria-label="Stäng meddelande"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>
        </div>
        {message ? (
          <div
            className={`overflow-hidden transition-[max-height,margin] duration-300 ease-out ${
              expanded ? "mt-3 max-h-[320px]" : "mt-0 max-h-0"
            }`}
          >
            {showMessageContent ? (
              <div className="whitespace-pre-line text-sm leading-relaxed">
                {message}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function AnnouncementCard({
  announcement,
  expanded,
  isClosing = false,
  onToggleExpand,
  onDismiss,
}) {
  const variant = ANNOUNCEMENT_VARIANTS[announcement?.level] || ANNOUNCEMENT_VARIANTS.info;
  const AnnouncementIcon = variant.icon;
  const title = String(announcement?.title || "").trim() || "Meddelande";
  const message = String(announcement?.message || "").trim();
  const canExpand = message.length > 140 || message.includes("\n");
  const [dismissPulse, setDismissPulse] = React.useState(false);

  const handleDismissClick = React.useCallback(() => {
    setDismissPulse(true);
    window.setTimeout(() => {
      setDismissPulse(false);
      onDismiss?.();
    }, 180);
  }, [onDismiss]);

  return (
    <div
      className={[
        "relative origin-center overflow-hidden rounded-2xl border shadow-sm transform-gpu transition-[opacity,margin,transform] duration-200 ease-out",
        variant.cardClass,
        isClosing
          ? "pointer-events-none my-0 opacity-0"
          : "my-0.5 scale-x-100 scale-y-100 opacity-100",
      ].join(" ")}
      style={{
        transform: dismissPulse ? "translateZ(0) scale(1.02)" : undefined,
        transitionTimingFunction: dismissPulse
          ? "cubic-bezier(0.2, 0.9, 0.25, 1.35)"
          : "cubic-bezier(0.22, 1, 0.36, 1)",
      }}
    >
      <Accordion
        type="single"
        collapsible
        value={expanded ? "announcement" : ""}
        onValueChange={(value) => {
          if ((value === "announcement") !== expanded) {
            onToggleExpand();
          }
        }}
      >
        <AccordionItem value="announcement" className="border-none">
          <div className="p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <AnnouncementIcon className={`h-5 w-5 shrink-0 ${variant.iconClass}`} aria-hidden />
                  <span className={`min-w-0 truncate text-sm font-semibold leading-snug ${variant.softClass}`}>
                    {title}
                  </span>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {canExpand ? (
                  <button
                    type="button"
                    onClick={onToggleExpand}
                    className={`inline-flex h-8 w-8 items-center justify-center rounded-full border transition ${variant.buttonClass}`}
                    aria-label={expanded ? "Minimera meddelande" : "Expandera meddelande"}
                  >
                    <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
                  </button>
                ) : null}
                {announcement?.dismissible ? (
                  <button
                    type="button"
                    onClick={handleDismissClick}
                    className={`relative inline-flex h-8 w-8 items-center justify-center rounded-full border transition-transform duration-200 active:scale-110 ${variant.buttonClass}`}
                    aria-label="Stäng meddelande"
                  >
                    <X className="h-4 w-4" />
                  </button>
                ) : null}
              </div>
            </div>
            {message ? (
              <AccordionContent className="mt-3 pb-0 pt-0 whitespace-pre-line text-sm leading-relaxed">
                {message}
              </AccordionContent>
            ) : null}
          </div>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

function getOfficialMatchPill(match, { todayLabel = "Idag", upcomingLabel = "Kommande" } = {}) {
  if (isManualCancelled(match)) {
    return {
      label: "Inställd",
      className: "bg-destructive text-destructive-foreground",
    };
  }

  const matchDate = toDate(match?.date);
  const isToday = matchDate && !Number.isNaN(matchDate.getTime()) && isSameSvDay(matchDate, new Date());
  if (isToday) {
    return {
      label: todayLabel,
      className: "bg-emerald-500/15 text-emerald-800 border border-emerald-500/30 dark:text-emerald-300",
    };
  }

  const status = String(match?.status || "").trim().toLowerCase();
  if (status === "upcoming" || !status) {
    return {
      label: upcomingLabel,
      className: "bg-primary text-[hsl(var(--primary-foreground))]",
    };
  }

  return {
    label: status,
    className: "bg-primary text-[hsl(var(--primary-foreground))]",
  };
}

function MatchRow({ round, home, away, time, match, onClick }) {
  const pill = getOfficialMatchPill(match);

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center justify-between py-3 text-left transition-colors hover:bg-secondary/30"
    >
      <div className="flex items-center gap-3 min-w-0">
        {round ? (
          <Badge className="bg-[hsl(var(--secondary))] text-white border border-[hsl(var(--border-alt))]">
            {round}
          </Badge>
        ) : null}
        <div className="truncate">
          <div className="text-foreground truncate">
            {home} <span className="text-[hsl(var(--muted))]">vs</span> {away}
          </div>
          <div className="text-xs text-[hsl(var(--muted))] flex items-center gap-1">
            <CalendarDays className="h-3 w-3" />
            {time}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {pill?.label ? (
          <Badge className={pill.className}>{pill.label}</Badge>
        ) : null}
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border text-muted-foreground">
          <ChevronRight className="h-5 w-5 " />
        </span>
      </div>
    </button>
  );
}

function getOfficialTeamDisplay(match, side) {
  if (!match) return "-";
  const display = match[`${side}_team_display`];
  const canonical = match[`${side}_team_name_canonical`];
  const raw = match[`${side}_team`];
  return display || canonical || raw || "-";
}



function HeroCarousel({ sameDayAll, fmtSv, onApiReady, onOpenProtocol }) {
  const [api, setApi] = React.useState(null);

  // Tider
  const AUTOPLAY_DELAY = 6300;   // ms mellan auto-skiften
  const RESUME_DELAY = AUTOPLAY_DELAY / 3; // hur länge efter interaktion innan autoplay startar igen
  const prefersReduced =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Koppla Embla API
  React.useEffect(() => {
    if (!api) return;
    onApiReady && onApiReady(api);
    return () => onApiReady && onApiReady(null);
  }, [api, onApiReady]);

  // Autoplay med robust paus/reset
  React.useEffect(() => {
    if (!api) return;
    if (prefersReduced) return;
    if (!(sameDayAll?.length > 1)) return;

    let nextId = null;   // setTimeout-id för nästa autosteg
    let resumeId = null; // setTimeout-id för resume efter paus
    let paused = false;

    const clearAll = () => {
      if (nextId) { clearTimeout(nextId); nextId = null; }
      if (resumeId) { clearTimeout(resumeId); resumeId = null; }
    };

    const step = () => {
      if (paused) return;
      if (api.canScrollNext()) api.scrollNext();
      else api.scrollTo(0);
      scheduleNext(); // schemalägg nästa steg igen
    };

    const scheduleNext = (delay = AUTOPLAY_DELAY) => {
      clearAll();
      if (paused) return;
      nextId = setTimeout(step, delay);
    };

    const pause = () => {
      paused = true;
      clearAll();
    };

    const resumeAfter = (ms = RESUME_DELAY) => {
      clearAll();
      paused = false;
      resumeId = setTimeout(() => {
        // börja räkna full delay innan nästa steg
        scheduleNext(AUTOPLAY_DELAY);
      }, ms);
    };

    const resetFullDelay = () => {
      // kör full väntetid efter varje manuell eller auto-"select"
      if (paused) return;
      scheduleNext(AUTOPLAY_DELAY);
    };

    // Root-node events
    const root = api.rootNode?.() || api.containerNode?.();

    // Pausa om användaren interagerar
    const onPointerDown = () => { pause(); };
    const onPointerUp = () => { resumeAfter(); };
    const onWheel = () => { pause(); resumeAfter(); };
    const onTouchStart = () => { pause(); resumeAfter(); };
    const onKeyDown = () => { pause(); resumeAfter(); };

    root?.addEventListener?.("pointerdown", onPointerDown, { passive: true });
    root?.addEventListener?.("pointerup", onPointerUp, { passive: true });
    root?.addEventListener?.("wheel", onWheel, { passive: true });
    root?.addEventListener?.("touchstart", onTouchStart, { passive: true });
    root?.addEventListener?.("keydown", onKeyDown);

    // Hover/focus pausar utan auto-resume tills lämnar
    const onEnter = () => pause();
    const onLeave = () => resumeAfter(300); // liten grace innan vi startar klockan
    root?.addEventListener?.("mouseenter", onEnter);
    root?.addEventListener?.("focusin", onEnter);
    root?.addEventListener?.("mouseleave", onLeave);
    root?.addEventListener?.("focusout", onLeave);

    // Embla events
    const onSelect = () => resetFullDelay();
    const onPointerDownEmbla = () => pause();
    const onPointerUpEmbla = () => resumeAfter();

    api.on("select", onSelect);
    api.on("reInit", onSelect);
    api.on("pointerDown", onPointerDownEmbla);
    api.on("pointerUp", onPointerUpEmbla);

    // Flik synlighet
    const onVis = () => {
      if (document.hidden) pause();
      else resumeAfter(500);
    };
    document.addEventListener("visibilitychange", onVis);

    // Start
    scheduleNext(AUTOPLAY_DELAY);

    return () => {
      clearAll();
      document.removeEventListener("visibilitychange", onVis);

      root?.removeEventListener?.("pointerdown", onPointerDown);
      root?.removeEventListener?.("pointerup", onPointerUp);
      root?.removeEventListener?.("wheel", onWheel);
      root?.removeEventListener?.("touchstart", onTouchStart);
      root?.removeEventListener?.("keydown", onKeyDown);

      root?.removeEventListener?.("mouseenter", onEnter);
      root?.removeEventListener?.("focusin", onEnter);
      root?.removeEventListener?.("mouseleave", onLeave);
      root?.removeEventListener?.("focusout", onLeave);

      try { api.off("select", onSelect); } catch { }
      try { api.off("reInit", onSelect); } catch { }
      try { api.off("pointerDown", onPointerDownEmbla); } catch { }
      try { api.off("pointerUp", onPointerUpEmbla); } catch { }
    };
  }, [api, sameDayAll?.length, prefersReduced]);

  return (
    <Carousel
      setApi={setApi}
      opts={{ align: "start", loop: true, duration: 40 }}
      className="cursor-grab active:cursor-grabbing"
    >
      <CarouselContent className="mt-3">
        {sameDayAll?.length ? sameDayAll.map((m) => {
          const homeTeam = getOfficialTeamDisplay(m, "home");
          const awayTeam = getOfficialTeamDisplay(m, "away");
          const dateLabel = formatSvDate(m.date, {
            weekday: "short",
            day: "numeric",
            month: "short",
          });
          const timeLabel = formatSvTime(m.date, {
            hour: "2-digit",
            minute: "2-digit",
          });
          return (
            <CarouselItem key={m.id || m._id} className="basis-full">
              <div className="px-3">
                <div className="mt-3 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-start gap-3">
                  <div className="flex min-w-0 flex-1 flex-col items-start">
                    <TeamLogo
                      teamName={homeTeam}
                      size={44}
                      className="mb-2 h-11 w-11 shrink-0 object-contain"
                    />
                    <div className="w-full min-w-0 truncate text-[clamp(1.3rem,6vw,2.25rem)] font-bold leading-[1.05] text-muted-foreground">
                      {homeTeam}
                    </div>
                  </div>
                  <div className="pt-4 text-[hsl(var(--muted))] font-bold text-lg shrink-0">vs</div>
                  <div className="flex min-w-0 flex-1 flex-col items-end text-right">
                    <TeamLogo
                      teamName={awayTeam}
                      size={44}
                      className="mb-2 h-11 w-11 shrink-0 object-contain"
                    />
                    <div className="w-full min-w-0 truncate text-[clamp(1.3rem,6vw,2.25rem)] font-bold leading-[1.05] text-muted-foreground">
                      {awayTeam}
                    </div>
                  </div>
                </div>
                <div className="mt-8 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm font-medium text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5">
                    <CalendarDays className="h-4 w-4 shrink-0" />
                    <span>{dateLabel}</span>
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Clock3 className="h-4 w-4 shrink-0" />
                    <span>{timeLabel}</span>
                  </span>
                </div>
                <div className="mt-4 flex gap-2">
                  <Button
                    type="button"
                    onClick={() => onOpenProtocol(m.id || m._id)}
                    className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary-hover))] text-[hsl(var(--primary-foreground))]"
                  >
                    Se matchkort
                  </Button>
                </div>
              </div>
            </CarouselItem>
          );
        }) : (
          <CarouselItem className="basis-full">
            <div className="px-5 mt-3 text-[hsl(var(--muted))]">Inga kommande officiella matcher</div>
          </CarouselItem>
        )}
      </CarouselContent>
    </Carousel>
  );
}





/* ---------- Huvudsida ---------- */
export default function HomePage() {
  const navigate = useNavigate();
  const isNativeAndroid = getNativePlatform() === "android";
  const resumePromptOpen = false;
  const setResumePromptOpen = () => {};
  const clearActiveProtocol = () => {};
  const openShell = () => {};
  const resumeProtocolSession = () => {};
  const handleOpenProtocolFromHome = useOpenOfficialMatch();

  // OFFICIELLA matcher -> HERO + kommande-lista
  const { data: officialMatches = [], isLoading: loadingOfficial, isError: errorOfficial } =
    useOfficialMatches({ includeUsed: true, staleTime: 15 * 60_000, refetchOnWindowFocus: false });

  // Egna matcher (skapade av användare) -> lättvikts-sammanfattning för Home
  const {
    data: matchesSummary,
    isLoading: loadingMatches,
    isError: errorMatches,
  } = useMatchesSummary({
    recentLimit: 10,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
  const matches = Array.isArray(matchesSummary?.recent) ? matchesSummary.recent : [];
  const myMatchesCount = Number(matchesSummary?.count || 0);

  // Bevakningsfunktionalitet ej implementerad än.
  const loadingUserMatches = false;

  const season = getConfiguredSeason();
  const {
    data: standingsData,
    isLoading: loadingStandings,
    isError: errorStandings,
  } = useStandings(season, {
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    placeholderData: (prev) => prev,
    refetchOnWindowFocus: false,
  });
  const {
    data: playoffsData,
    isLoading: loadingPlayoffs,
  } = usePlayoffs(season, {
    enabled: isNativeAndroid,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    placeholderData: (prev) => prev,
    refetchOnWindowFocus: false,
  });

  const [dismissedAnnouncementIds, setDismissedAnnouncementIds] = React.useState(() => {
    if (typeof window === "undefined") return null;
    try {
      const raw = localStorage.getItem(ANNOUNCEMENT_STORAGE_KEY)
        || sessionStorage.getItem(ANNOUNCEMENT_STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (err) {
      return [];
    }
  });
  const [expandedAnnouncementIds, setExpandedAnnouncementIds] = useState([]);
  const [closingAnnouncementIds, setClosingAnnouncementIds] = useState([]);

  const { announcements: currentAnnouncements } = useSystemAnnouncements({
    refetchOnMount: false,
  });

  const announcements = React.useMemo(() => {
    return currentAnnouncements.filter((announcement) => {
      const announcementId = `${announcement?.id || ""}:${announcement?.version || 0}`;
      if (announcement?.dismissible && dismissedAnnouncementIds?.includes?.(announcementId)) return false;
      return true;
    });
  }, [currentAnnouncements, dismissedAnnouncementIds]);

  const handleDismissAnnouncement = React.useCallback((announcement) => {
    const announcementId = `${announcement?.id || ""}:${announcement?.version || 0}`;
    if (!announcement?.dismissible || !announcementId) return;
    setExpandedAnnouncementIds((prev) => prev.filter((id) => id !== announcementId));
    setClosingAnnouncementIds((prev) => (prev.includes(announcementId) ? prev : [...prev, announcementId]));

    window.setTimeout(() => {
      setDismissedAnnouncementIds((prev) => {
        const next = Array.isArray(prev) ? Array.from(new Set([...prev, announcementId])) : [announcementId];
        try {
          localStorage.setItem(ANNOUNCEMENT_STORAGE_KEY, JSON.stringify(next));
          sessionStorage.removeItem(ANNOUNCEMENT_STORAGE_KEY);
        } catch (err) {
          /* ignore */
        }
        return next;
      });
      setClosingAnnouncementIds((prev) => prev.filter((id) => id !== announcementId));
    }, 220);
  }, []);

  const toggleAnnouncementExpanded = React.useCallback((announcementId) => {
    setExpandedAnnouncementIds((prev) =>
      prev.includes(announcementId)
        ? prev.filter((id) => id !== announcementId)
        : [...prev, announcementId]
    );
  }, []);

  const now = new Date();

  // Kommande OFFICIELLA matcher. Matcher samma dag ligger kvar till cutoff i queries/matches.
  const officialUpcoming = React.useMemo(() => {
    if (!Array.isArray(officialMatches)) return [];
    const seasonMatches = officialMatches.filter((m) => {
      const d = toDate(m?.date);
      return Number(m?.season) === season && d && !Number.isNaN(d.getTime());
    });
    return selectUpcomingByDate(now)(seasonMatches);
  }, [officialMatches, now, season]);

  const OFFICIAL_UPCOMING_BATCH_SIZE = 10;
  const [officialUpcomingVisibleCount, setOfficialUpcomingVisibleCount] = React.useState(OFFICIAL_UPCOMING_BATCH_SIZE);

  React.useEffect(() => {
    setOfficialUpcomingVisibleCount(OFFICIAL_UPCOMING_BATCH_SIZE);
  }, [officialUpcoming.length]);

  const displayedOfficialUpcoming = React.useMemo(
    () => officialUpcoming.slice(0, officialUpcomingVisibleCount),
    [officialUpcoming, officialUpcomingVisibleCount]
  );

  const hasMoreOfficialUpcoming = officialUpcoming.length > displayedOfficialUpcoming.length;

  const recentFinishedMatches = React.useMemo(() => {
    if (!Array.isArray(officialMatches)) return [];
    return officialMatches
      .filter((match) => {
        const matchDate = toDate(match?.date);
        const matchSeason = Number(match?.season);
        return (
          matchDate &&
          !Number.isNaN(matchDate.getTime()) &&
          matchSeason === season &&
          match?.status === "finished" &&
          match?.home_score != null &&
          match?.away_score != null
        );
      })
      .sort((a, b) => {
        const da = toDate(a?.date);
        const db = toDate(b?.date);
        return (db?.getTime() || 0) - (da?.getTime() || 0);
      });
  }, [officialMatches, season]);

  const RECENT_RESULTS_BATCH_SIZE = 6;
  const [recentResultsVisibleCount, setRecentResultsVisibleCount] = React.useState(RECENT_RESULTS_BATCH_SIZE);
  const [isLoadingMoreRecentResults, setIsLoadingMoreRecentResults] = React.useState(false);
  const [canScrollRecentResultsPrev, setCanScrollRecentResultsPrev] = React.useState(false);
  const [canScrollRecentResultsNext, setCanScrollRecentResultsNext] = React.useState(false);
  const recentResultsLoadTimerRef = React.useRef(null);
  const recentResultsScrollerRef = React.useRef(null);
  const recentResultsLoaderRef = React.useRef(null);

  React.useEffect(() => {
    setRecentResultsVisibleCount(RECENT_RESULTS_BATCH_SIZE);
    setIsLoadingMoreRecentResults(false);
    if (recentResultsLoadTimerRef.current) {
      window.clearTimeout(recentResultsLoadTimerRef.current);
      recentResultsLoadTimerRef.current = null;
    }
  }, [recentFinishedMatches.length]);

  const displayedRecentFinishedMatches = React.useMemo(
    () => recentFinishedMatches.slice(0, recentResultsVisibleCount),
    [recentFinishedMatches, recentResultsVisibleCount]
  );

  const hasMoreRecentResults = recentResultsVisibleCount < recentFinishedMatches.length;

  const loadMoreRecentResults = React.useCallback(() => {
    if (isLoadingMoreRecentResults || !hasMoreRecentResults) return;

    setIsLoadingMoreRecentResults(true);
    recentResultsLoadTimerRef.current = window.setTimeout(() => {
      setRecentResultsVisibleCount((current) =>
        Math.min(current + RECENT_RESULTS_BATCH_SIZE, recentFinishedMatches.length)
      );
      setIsLoadingMoreRecentResults(false);
      recentResultsLoadTimerRef.current = null;
    }, 220);
  }, [hasMoreRecentResults, isLoadingMoreRecentResults, recentFinishedMatches.length]);

  const updateRecentResultsScrollButtons = React.useCallback(() => {
    const el = recentResultsScrollerRef.current;
    if (!el) return;
    const maxScrollLeft = el.scrollWidth - el.clientWidth;
    setCanScrollRecentResultsPrev(el.scrollLeft > 8);
    setCanScrollRecentResultsNext(maxScrollLeft - el.scrollLeft > 8);
  }, []);

  const scrollRecentResultsByPage = React.useCallback((direction) => {
    const el = recentResultsScrollerRef.current;
    if (!el) return;
    const amount = Math.max(220, Math.round(el.clientWidth * 0.82));
    el.scrollBy({
      left: direction === "next" ? amount : -amount,
      behavior: "smooth",
    });
  }, []);

  React.useEffect(() => {
    const el = recentResultsScrollerRef.current;
    if (!el || isLoadingMoreRecentResults || !hasMoreRecentResults) return;

    if (el.scrollWidth <= el.clientWidth + 24) {
      loadMoreRecentResults();
    }
  }, [displayedRecentFinishedMatches.length, hasMoreRecentResults, isLoadingMoreRecentResults, loadMoreRecentResults]);

  React.useEffect(() => {
    const root = recentResultsScrollerRef.current;
    const target = recentResultsLoaderRef.current;
    if (!root || !target || !hasMoreRecentResults) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          loadMoreRecentResults();
        }
      },
      {
        root,
        threshold: 0.85,
      }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [displayedRecentFinishedMatches.length, hasMoreRecentResults, loadMoreRecentResults]);

  React.useEffect(() => {
    const el = recentResultsScrollerRef.current;
    if (!el) return;

    const handleScroll = () => updateRecentResultsScrollButtons();
    handleScroll();
    el.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);

    return () => {
      el.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [displayedRecentFinishedMatches.length, updateRecentResultsScrollButtons]);

  React.useEffect(() => {
    return () => {
      if (recentResultsLoadTimerRef.current) {
        window.clearTimeout(recentResultsLoadTimerRef.current);
        recentResultsLoadTimerRef.current = null;
      }
    };
  }, []);

  // För hero-kortet tar vi nästa officiella
  const nextOfficial = officialUpcoming[0] || null;

  // Bygg lista med matcher samma datum som nextOfficial (hero-matchen först),
  // ingen match med senare datum inkluderas.
  const sameDayAll = React.useMemo(() => {
    if (!nextOfficial) return [];
    const sameDate = officialUpcoming
      .filter((m) => isSameSvDay(m.date, nextOfficial.date))
      .sort((a, b) => {
        const da = toDate(a.date);
        const db = toDate(b.date);
        return (da?.getTime() || 0) - (db?.getTime() || 0);
      });
    const heroId = nextOfficial.id || nextOfficial._id;
    const rest = sameDate.filter((m) => (m.id || m._id) !== heroId);
    return [nextOfficial, ...rest];
  }, [officialUpcoming, nextOfficial]);

  // Carousel-index för hero-kortet
  const [heroIdx, setHeroIdx] = React.useState(0);




  // Nollställ index när listan byts (t.ex. när datum byts eller laddning klart)
  React.useEffect(() => {
    setHeroIdx(0);
  }, [sameDayAll?.length]);

  // Bekvämlighet: aktuell match i hero
  const heroMatch = sameDayAll[heroIdx] || nextOfficial || null;
  const heroPill = heroMatch ? getOfficialMatchPill(heroMatch, { upcomingLabel: "Kommande" }) : null;
  const standingsRows = Array.isArray(standingsData?.rows) ? standingsData.rows : [];

  // --- Tabellplaceringar för snabbstatistik ---
  const norm = (s) => (s || "").toString().trim().toLowerCase();

  // Använd endast officiell tabell för snabbstatistik för att undvika "hoppande" placeringar.
  const rankSource = standingsRows.map((r) => ({
    teamId: r.team_id,
    team: r.team,
    points: r.points,
    position: r.position,
  }));

  const findStandingRow = (teamId, fallbackName) => {
    if (!rankSource.length) return null;
    if (teamId) {
      const byId = rankSource.find((t) => String(t.teamId) === String(teamId));
      if (byId) return byId;
    }
    if (!fallbackName) return null;
    return rankSource.find((t) => norm(t.team) === norm(fallbackName)) || null;
  };

  const homeStanding = heroMatch
    ? findStandingRow(heroMatch.home_team_id, heroMatch.home_team_name_canonical || heroMatch.home_team)
    : null;
  const awayStanding = heroMatch
    ? findStandingRow(heroMatch.away_team_id, heroMatch.away_team_name_canonical || heroMatch.away_team)
    : null;

  const homeRank = homeStanding?.position ?? null;
  const awayRank = awayStanding?.position ?? null;
  const homePts = homeStanding?.points ?? null;
  const awayPts = awayStanding?.points ?? null;

  const computeLatestMeetingInSeason = (matches, teamA, teamB, year, currentMatchDate) => {
    if (!Array.isArray(matches) || !teamA || !teamB || !year || !currentMatchDate) return null;
    const currentTime = currentMatchDate.getTime();
    const relevant = matches
      .filter((m) => {
        const d = toDate(m.date);
        const homeIdentity = m.home_team_id || m.home_team_name_canonical || m.home_team;
        const awayIdentity = m.away_team_id || m.away_team_name_canonical || m.away_team;
        return (
          d &&
          !Number.isNaN(d.getTime()) &&
          d.getTime() < currentTime &&
          getSvYear(d) === year &&
          [homeIdentity, awayIdentity].includes(teamA) &&
          [homeIdentity, awayIdentity].includes(teamB) &&
          m.home_score != null &&
          m.away_score != null
        );
      })
      .sort((a, b) => {
        const da = toDate(a.date);
        const db = toDate(b.date);
        return (db?.getTime() || 0) - (da?.getTime() || 0);
      });

    const latest = relevant[0];
    if (!latest) return null;

    const latestHomeIdentity = latest.home_team_id || latest.home_team_name_canonical || latest.home_team;
    const latestAwayIdentity = latest.away_team_id || latest.away_team_name_canonical || latest.away_team;
    const latestHomeTeam = getOfficialTeamDisplay(latest, "home");
    const latestAwayTeam = getOfficialTeamDisplay(latest, "away");

    if (
      (latestHomeIdentity === teamA && latestAwayIdentity === teamB) ||
      (latestHomeIdentity === teamB && latestAwayIdentity === teamA)
    ) {
      return {
        homeTeam: latestHomeTeam,
        awayTeam: latestAwayTeam,
        homeScore: latest.home_score,
        awayScore: latest.away_score,
      };
    }

    return null;
  };

  const seasonYear = heroMatch ? getSvYear(heroMatch.date) : null;
  const homeIdentity = heroMatch
    ? heroMatch.home_team_id || heroMatch.home_team_name_canonical || heroMatch.home_team
    : null;
  const awayIdentity = heroMatch
    ? heroMatch.away_team_id || heroMatch.away_team_name_canonical || heroMatch.away_team
    : null;
  const heroMatchDate = heroMatch ? toDate(heroMatch.date) : null;
  const latestMeeting =
    homeIdentity && awayIdentity && seasonYear && heroMatchDate
      ? computeLatestMeetingInSeason(
        officialMatches,
        homeIdentity,
        awayIdentity,
        seasonYear,
        heroMatchDate
      )
      : null;

  // Helpers för navigering
  // const canPrev = heroIdx > 0;
  // const canNext = heroIdx < sameDayAll.length - 1;
  // const goPrev = () => canPrev && setHeroIdx((i) => i - 1);
  // const goNext = () => canNext && setHeroIdx((i) => i + 1);

  // Topplag enbart från tabell-data (ingen fallback)
  const topTeamSummary = useMemo(() => {
    if (!Array.isArray(standingsRows) || standingsRows.length === 0) {
      return {
        title: "Topplag",
        value: "–",
        hint: "Ledare i tabellen",
      };
    }

    const topPoints = Number(standingsRows[0]?.points ?? 0);
    const leaders = standingsRows.filter((row) => Number(row?.points ?? 0) === topPoints);

    if (leaders.length <= 1) {
      return {
        title: "Topplag",
        value: leaders[0]?.team || "–",
        hint: "Ledare i tabellen",
      };
    }

    if (leaders.length === 2) {
      return {
        title: "Delad serieledning",
        value: leaders.map((row) => row.team).join(" & "),
        hint: `${topPoints} poäng`,
      };
    }

    return {
      title: "Delad serieledning",
      value: leaders.map((row) => row.team).join(", "),
      hint: `${topPoints} poäng`,
    };
  }, [standingsRows]);

  const overviewCards = (
    <>
      {loadingUserMatches ? (
        <StatCardSkeleton />
      ) : (
        <StatCard title="Mina bevakningar" value="-" hint="Kommer snart" icon={<Users className="h-4 w-4" />} />
      )}

      {loadingMatches ? (
        <StatCardSkeleton />
      ) : (
        <StatCard
          title="Protokoll"
          value={String(myMatchesCount)}
          hint="Mina protokoll"
          icon={<ListIcon className="h-4 w-4" />}
          onClick={() => navigate("/account/matches")}
        />
      )}

      {loadingStandings ? (
        <StatCardSkeleton />
      ) : (
        <StatCard
          title={topTeamSummary.title}
          value={topTeamSummary.value}
          hint={topTeamSummary.hint}
          icon={<Trophy className="h-4 w-4" />}
          onClick={() => navigate("/tabell/tabell")}
        />
      )}
    </>
  );

  const tableTeams = standingsRows.length
    ? standingsRows.map((r) => ({
      id: r.team_id || r.team,
      name: r.team,
      city: r.city || "",
      matches_played: r.played,
      points: r.points,
    }))
    : [];

  // Hjälpare för datumformat
  const fmtSv = (iso) =>
    formatSvDateTime(iso, {
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });



  // i HomePage-komponenten, ovanför return:
  const [heroApi, setHeroApi] = React.useState(null);
  React.useEffect(() => {
    if (!heroApi) return;
    // sätt initial index + lyssna på byten
    const setFromApi = () => setHeroIdx(heroApi.selectedScrollSnap());
    setFromApi();
    heroApi.on("select", setFromApi);
    return () => {
      try { heroApi.off("select", setFromApi); } catch { }
    };
  }, [heroApi]);

  if (isNativeAndroid) {
    return (
      <div className="mx-auto max-w-7xl">
        <Seo
          title="Lapstr - speedway, resultat och digitala matchprotokoll"
          description="Följ speedway med kommande matcher, tabell, serieöversikt och digitala matchprotokoll i Lapstr."
          canonicalPath="/"
        />
        <PageTutorial pageName="homeOnboarding" />
        <NativeHomePage
            announcements={announcements}
            expandedAnnouncementIds={expandedAnnouncementIds}
            closingAnnouncementIds={closingAnnouncementIds}
            onToggleAnnouncement={toggleAnnouncementExpanded}
            onDismissAnnouncement={handleDismissAnnouncement}
            loadingOfficial={loadingOfficial}
            errorOfficial={errorOfficial}
            heroMatches={sameDayAll}
            heroIndex={heroIdx}
            onHeroApi={setHeroApi}
            onOpenMatch={handleOpenProtocolFromHome}
            loadingMatches={loadingMatches}
            protocolCount={String(myMatchesCount)}
            loadingStandings={loadingStandings}
            topTeamSummary={topTeamSummary}
            onNavigate={navigate}
            recentMatches={recentFinishedMatches}
            displayedRecentMatches={displayedRecentFinishedMatches}
            recentScrollerRef={recentResultsScrollerRef}
            recentLoaderRef={recentResultsLoaderRef}
            hasMoreRecent={hasMoreRecentResults}
            loadingMoreRecent={isLoadingMoreRecentResults}
            displayedUpcoming={displayedOfficialUpcoming}
            hasMoreUpcoming={hasMoreOfficialUpcoming}
            onShowMoreUpcoming={() =>
              setOfficialUpcomingVisibleCount((count) => count + OFFICIAL_UPCOMING_BATCH_SIZE)
            }
            standingsRows={standingsRows}
            officialMatches={officialMatches}
            playoffStages={Array.isArray(playoffsData?.stages) ? playoffsData.stages : []}
            playoffFormat={playoffsData?.format || null}
            loadingPlayoffs={loadingPlayoffs}
        />
      </div>
    );
  }



  return (
    <div className="mx-auto max-w-7xl">
      <Seo
        title="Lapstr - speedway, resultat och digitala matchprotokoll"
        description="Följ speedway med kommande matcher, tabell, serieöversikt och digitala matchprotokoll i Lapstr."
        canonicalPath="/"
      />
      <PageTutorial pageName="homeOnboarding" />
      {announcements.length ? (
        <div className="mb-6">
          <div className="space-y-3">
            {announcements.map((announcement) => {
              const announcementId = `${announcement?.id || ""}:${announcement?.version || 0}`;
              return (
                <AnnouncementCard
                  key={announcementId}
                  announcement={announcement}
                  expanded={expandedAnnouncementIds.includes(announcementId)}
                  isClosing={closingAnnouncementIds.includes(announcementId)}
                  onToggleExpand={() => toggleAnnouncementExpanded(announcementId)}
                  onDismiss={() => handleDismissAnnouncement(announcement)}
                />
              );
            })}
          </div>
        </div>
      ) : null}
      {/* HERO – OFFICIELL nästa match */}
      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_320px] lg:items-stretch lg:gap-4">
        <section className="w-full lg:h-full">
          <Card className="overflow-visible rounded-[var(--radius)] border border--border bg-gradient-to-r from-[hsl(var(--card))] to-[hsl(var(--alt-bg))] lg:h-full">
            <CardContent id="todayMatches" className="p-0 lg:h-full">
              {errorOfficial ? (
                <div className="p-4">
                  <p className="text-red-500 text-sm">Kunde inte ladda matcher.</p>
                </div>
              ) : loadingOfficial ? (
                <HeroSkeleton />
              ) : (
                <div className="flex flex-col md:flex-row lg:h-full">
                  <div className="relative min-w-0 flex-1 overflow-visible px-1 py-5">
                    <Badge className={`${heroPill?.className || "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]"} ml-4`}>
                      {sameDayAll.length ? heroPill?.label || "Kommande" : "Info"}
                    </Badge>
                    <span className="ml-2 text-muted text-sm">
                      {sameDayAll.length ? (sameDayAll[0].league || "Matcher") : "Matcher"}
                      {sameDayAll.length && sameDayAll[0].round ? ` • ${sameDayAll[0].round}` : ""}
                    </span>

                    <div className="mt-2 overflow-hidden">
                      <HeroCarousel
                        sameDayAll={sameDayAll}
                        fmtSv={fmtSv}
                        onApiReady={setHeroApi}
                        onOpenProtocol={handleOpenProtocolFromHome}
                      />
                    </div>
                    {sameDayAll.length > 1 && (
                      <div className="mt-4 ml-5 flex items-center gap-1.5">
                        {sameDayAll.map((_, i) => (
                          <button
                            key={i}
                            aria-label={`Gå till match ${i + 1}`}
                            onClick={() => heroApi && heroApi.scrollTo(i)}
                            className={`h-1.5 rounded-full transition-all ${i === heroIdx ? "w-4 bg-[hsl(var(--primary))]" : "w-2 bg-[hsl(var(--border))]"}`}
                          />
                        ))}
                        <span className="ml-2 text-[11px] text-[hsl(var(--muted))]">
                          {heroIdx + 1}/{sameDayAll.length}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col rounded-b-[var(--radius)] border-[hsl(var(--border))] bg-[hsl(var(--alt-bg))] p-4 md:w-72 md:border-l md:rounded-r-[var(--radius)] md:rounded-b-none lg:h-full">
                    <div className="text-[hsl(var(--muted))] text-xs uppercase tracking-wide">Snabbstatistik</div>
                    <div className="mt-3 grid grid-cols-3 gap-2 text-white md:grid-cols-2">
                      <div className="flex min-h-[120px] flex-col rounded-sm bg-[hsl(var(--card))] px-3 py-2 text-center">
                        <div className="text-xs text-[hsl(var(--muted))] mb-1 truncate">Plac (H)</div>
                        <div className="flex flex-1 flex-col items-center justify-center">
                          <div className="text-2xl font-semibold text-foreground">{homeRank ?? "–"}</div>
                          <div className="text-[10px] text-[hsl(var(--muted))]">{homePts != null ? `${homePts}p` : ""}</div>
                        </div>
                      </div>
                      <div className="flex min-h-[120px] flex-col rounded-md bg-[hsl(var(--card))] px-3 py-2 text-center">
                        <div className="text-xs text-[hsl(var(--muted))] mb-1 truncate">Plac (B)</div>
                        <div className="flex flex-1 flex-col items-center justify-center">
                          <div className="text-2xl font-semibold text-foreground">{awayRank ?? "–"}</div>
                          <div className="text-[10px] text-[hsl(var(--muted))]">{awayPts != null ? `${awayPts}p` : ""}</div>
                        </div>
                      </div>
                      <div className="flex min-h-[120px] flex-col rounded-md bg-[hsl(var(--card))] px-3 py-2 text-center md:col-span-2">
                        <div className="text-xs text-[hsl(var(--muted))] mb-1 truncate">Senaste mötet</div>
                        <div className="flex flex-1 items-center justify-center">
                          {latestMeeting ? (
                            <div className="grid w-full grid-cols-2 gap-2">
                              <div className="flex flex-col items-center">
                                <div className="text-[10px] font-medium uppercase tracking-wide text-[hsl(var(--muted))]">H</div>
                                <TeamLogo
                                  teamName={latestMeeting.homeTeam}
                                  size={26}
                                  className="mt-1 h-6 w-6 object-contain"
                                />
                                <div className="mt-1 text-lg font-semibold text-foreground">{latestMeeting.homeScore}</div>
                              </div>
                              <div className="flex flex-col items-center">
                                <div className="text-[10px] font-medium uppercase tracking-wide text-[hsl(var(--muted))]">B</div>
                                <TeamLogo
                                  teamName={latestMeeting.awayTeam}
                                  size={26}
                                  className="mt-1 h-6 w-6 object-contain"
                                />
                                <div className="mt-1 text-lg font-semibold text-foreground">{latestMeeting.awayScore}</div>
                              </div>
                            </div>
                          ) : null}
                          {!latestMeeting ? (
                            <div className="text-2xl font-semibold text-foreground">–</div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </section>

        <section className="hidden h-full lg:block">
          <div className="grid h-full grid-cols-1 grid-rows-3 gap-3" id="homeStatsDesktop">
            {overviewCards}
          </div>
        </section>
      </div>

      <section className="mt-4">
        <AdSlot
          placement="banner_home"
          slotKey="home-hero"
          className="mx-auto"
          refreshMs={45000}
          minSwapMs={30000}
          lazy
        />
      </section>

      {/* Statistikkort */}

      {/* <section className="grid grid-cols-1  sm:grid-cols-3 gap-3" id="homeStats">
        <h2 className="text-xl font-bold ml-4 mt-4">Översikt</h2>
        {loadingUserMatches ? (
          <StatCardSkeleton />
        ) : (
          <StatCard title="Mina bevakningar" value="-" hint="Kommer snart" icon={<Users className="h-4 w-4" />} />
        )}

        {loadingMatches ? (
          <StatCardSkeleton />
        ) : (
          <StatCard
            title="Protokoll"
            value={String(myMatchesCount)}
            hint="Mina protokoll"
            icon={<ListIcon className="h-4 w-4" />}
            onClick={() => navigate("/account/matches")}
          />
        )}

        {loadingStandings ? (
          <StatCardSkeleton />
        ) : (
          <StatCard
            title="Topplag"
            value={topTeam || "–"}
            hint="Ledare i tabellen"
            icon={<Trophy className="h-4 w-4" />}
            onClick={() => navigate("/tabell/tabell")}
          />
        )}
      </section> */}

      <div className="lg:hidden">
        <div className="mb-2 mt-4">
          <h2 className="text-xl font-bold text-foreground">Översikt</h2>
        </div>
        <section className="grid grid-cols-1 sm:grid-cols-3 gap-3" id="homeStats">
          {overviewCards}
        </section>
      </div>

      {!loadingOfficial && recentFinishedMatches.length > 0 ? (
        <section className="mt-4">
          <div className="mb-2 flex items-center justify-between gap-3">
            <h2 className="text-xl font-bold text-foreground">Senaste resultat</h2>
            <div className="hidden items-center gap-2 lg:flex">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-9 w-9 rounded-full"
                onClick={() => scrollRecentResultsByPage("prev")}
                disabled={!canScrollRecentResultsPrev}
              >
                <ChevronLeft className="h-4 w-4" />
                <span className="sr-only">Föregående resultat</span>
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-9 w-9 rounded-full"
                onClick={() => scrollRecentResultsByPage("next")}
                disabled={!canScrollRecentResultsNext}
              >
                <ChevronRight className="h-4 w-4" />
                <span className="sr-only">Nästa resultat</span>
              </Button>
            </div>
          </div>

          <div
            ref={recentResultsScrollerRef}
            className="recent-results-rail -mx-4 flex items-start snap-x snap-mandatory gap-3 overflow-x-auto pb-2 pt-0.5 [scroll-padding-left:1rem] [scroll-padding-right:1rem] sm:mx-0 sm:[scroll-padding-left:0.25rem] sm:[scroll-padding-right:0.25rem]"
          >
              {displayedRecentFinishedMatches.map((match, index) => {
                const homeTeam = getOfficialTeamDisplay(match, "home");
                const awayTeam = getOfficialTeamDisplay(match, "away");
                const dateLabel = formatSvDate(match.date, {
                  day: "numeric",
                  month: "short",
                });

                return (
                  <article
                    key={match.id || match._id}
                    className={`min-w-[214px] shrink-0 snap-start [scroll-snap-stop:always] sm:min-w-[244px] lg:min-w-[256px] ${
                      index === 0 ? "ml-4 sm:ml-1" : ""
                    } ${
                      index === displayedRecentFinishedMatches.length - 1 && !hasMoreRecentResults ? "mr-4 sm:mr-1" : ""
                    }`}
                  >
                    <Card className="h-full border-border bg-card">
                      <CardContent className="p-2.5 sm:p-3">
                        <div className="mb-1.5 flex items-center justify-between gap-2 sm:mb-2">
                          <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                            {dateLabel}
                          </div>
                          <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                            resultat
                          </div>
                        </div>

                        <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2">
                          <div className="flex min-w-0 flex-col items-center text-center">
                            <TeamLogo
                              teamName={homeTeam}
                              size={26}
                              className="h-6.5 w-6.5 object-contain sm:h-8 sm:w-8"
                            />
                            <div className="mt-1 line-clamp-2 min-h-[1.75rem] text-[10px] font-medium leading-tight text-foreground sm:min-h-[2rem] sm:text-[11px]">
                              {homeTeam}
                            </div>
                          </div>

                          <div className="flex min-w-[60px] shrink-0 items-center justify-center px-1 py-0.5 sm:min-w-[68px] sm:py-1">
                            <div className="flex items-baseline gap-1.5">
                              <span className="text-base font-semibold tabular-nums text-foreground sm:text-xl">
                                {match.home_score}
                              </span>
                              <span className="text-[10px] font-medium text-muted-foreground">-</span>
                              <span className="text-base font-semibold tabular-nums text-foreground sm:text-xl">
                                {match.away_score}
                              </span>
                            </div>
                          </div>

                          <div className="flex min-w-0 flex-col items-center text-center">
                            <TeamLogo
                              teamName={awayTeam}
                              size={26}
                              className="h-6.5 w-6.5 object-contain sm:h-8 sm:w-8"
                            />
                            <div className="mt-1 line-clamp-2 min-h-[1.75rem] text-[10px] font-medium leading-tight text-foreground sm:min-h-[2rem] sm:text-[11px]">
                              {awayTeam}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </article>
                );
              })}
              {hasMoreRecentResults ? (
                <article
                  key="recent-results-loader"
                  ref={recentResultsLoaderRef}
                  className="mr-4 flex min-w-[72px] shrink-0 snap-start items-center justify-center self-stretch [scroll-snap-stop:always] sm:mr-1"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-full border border-border/80 bg-card/80 shadow-sm">
                    <LoaderCircle className={`h-5 w-5 text-muted-foreground ${isLoadingMoreRecentResults ? "animate-spin" : ""}`} />
                  </div>
                </article>
              ) : null}
          </div>
        </section>
      ) : null}

      {/* Flikar */}
      <section className="mt-6">
        <Tabs defaultValue="official" className="w-full">
          <div className="-mx-4 px-4 sm:mx-0 sm:px-0">
            <div className="overflow-x-auto no-scrollbar">
              <TabsList
                className="
                  inline-flex w-max
                  bg--card border border--border text-foreground
                  gap-1
                  rounded-radius
                  p-0
                  whitespace-nowrap
                "
              >
                <TabsTrigger
                  value="official"
                  className="
                    data-[state=active]:bg-primary data-[state=active]:text-primary-foreground
                    text-xs sm:text-sm px-3 py-2 sm:px-4
                  "
                >
                  Matcher (officiella)
                </TabsTrigger>

                <TabsTrigger
                  value="mine"
                  className="
                    data-[state=active]:bg-[hsl(var(--primary))] data-[state=active]:text-white
                    text-xs sm:text-sm px-3 py-2 sm:px-4
                  "
                >
                  Mina matcher
                </TabsTrigger>
              </TabsList>
            </div>
          </div>

          {/* OFFICIELLA MATCHER */}
          <TabsContent value="official" className="mt-4">
            <Card className="bg-card border-border ">
              <CardHeader>
                <CardTitle className="text-foreground">Kommande officiella matcher</CardTitle>
              </CardHeader>
              <CardContent className="divide-y divide-[hsl(var(--border))]">
                {errorOfficial ? (
                  <p className="text-red-500 text-sm">Kunde inte ladda officiella matcher.</p>
                ) : loadingOfficial ? (
                  <MatchesListSkeleton />
                ) : officialUpcoming.length > 0 ? (
                  <>
                    {displayedOfficialUpcoming.map((m) => {
                      const home = getOfficialTeamDisplay(m, "home");
                      const away = getOfficialTeamDisplay(m, "away");
                      return (
                        <MatchRow
                          key={m.id || m._id}
                          round={m.round || ""}
                          home={home}
                          away={away}
                          time={fmtSv(m.date)}
                          match={m}
                          onClick={() => handleOpenProtocolFromHome(m.id || m._id)}
                        />
                      );
                    })}
                    {hasMoreOfficialUpcoming ? (
                      <div className="pt-4">
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full"
                          onClick={() =>
                            setOfficialUpcomingVisibleCount((count) => count + OFFICIAL_UPCOMING_BATCH_SIZE)
                          }
                        >
                          Visa fler
                        </Button>
                      </div>
                    ) : null}
                  </>
                ) : (
                  <p className="text-[hsl(var(--muted))] text-sm py-2">Inga kommande officiella matcher.</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* EGNA MATCHER */}
          <TabsContent value="mine" className="mt-4">
            <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
              <CardHeader>
                <CardTitle className="text-foreground">Mina matcher</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="rounded-2xl border border-dashed border-border/80 bg-muted/20 p-5 text-sm text-muted-foreground">
                  Under utveckling. Här visas användarens bevakade matcher.
                </div>
              </CardContent>
            </Card>
          </TabsContent>

        </Tabs>
      </section>

      

      <AlertDialog open={resumePromptOpen} onOpenChange={setResumePromptOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Du har ett pågående protokoll</AlertDialogTitle>
            <AlertDialogDescription>
              Vill du återuppta det pågående protokollet eller starta om från matchkort?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction
              className="bg-secondary text-secondary-foreground hover:bg-secondary/80"
              onClick={() => {
                setResumePromptOpen(false);
                clearActiveProtocol();
                openShell();
              }}
            >
              Starta nytt
            </AlertDialogAction>
            <AlertDialogAction
              className="sm:ml-0"
              onClick={() => {
                setResumePromptOpen(false);
                resumeProtocolSession();
              }}
            >
              Återuppta
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
